import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Monitor } from "lucide-react";
import { scrollToId } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG = "https://images.unsplash.com/photo-1594886801340-88d2d9c028e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CAR_IMG = "https://images.unsplash.com/photo-1645400379459-f6fd3d963fd4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const LINES = [
  { text: "Skip the", cls: "text-white" },
  { text: "grind.", cls: "text-stroke-magenta" },
  { text: "Own the city.", cls: "text-[#a259ff] glow-cyan" },
];

const HeroTitle = () => (
  <h1 className="font-display text-[19vw] uppercase leading-[0.84] sm:text-[15vw] lg:text-[10.5rem] xl:text-[12rem]" data-testid="hero-title">
    {LINES.map((l, i) => (
      <span key={l.text} className="block overflow-hidden pb-[0.04em]">
        <motion.span
          className={`block ${l.cls}`}
          initial={{ y: "110%", rotate: 3 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{ duration: 1.15, ease: EASE, delay: 0.55 + i * 0.13 }}
        >
          {l.text}
        </motion.span>
      </span>
    ))}
  </h1>
);

const TiltFrame = ({ mx, my }) => {
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  return (
    <motion.div
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1100 }}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: EASE, delay: 1 }}
      className="relative aspect-[4/5] w-full"
    >
      <motion.div
        initial={{ clipPath: "inset(50% 0% 50% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.4, ease: EASE, delay: 1 }}
        className="absolute inset-0"
      >
      <div className="clip-corner absolute inset-0 overflow-hidden bg-[#0f0f16]">
        <img src={CAR_IMG} alt="Supercar under neon light" className="h-full w-full scale-110 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-transparent to-transparent" />
        <div className="scanlines absolute inset-0" />
      </div>
      </motion.div>
      <div className="absolute -left-4 bottom-8 bg-[#e9d5ff] px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-black" style={{ transform: "translateZ(60px)" }}>
        Modded / Ready to play
      </div>
      <div className="absolute -right-3 top-8 border border-[#a259ff]/60 bg-black/70 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-violet-200 backdrop-blur" style={{ transform: "translateZ(40px)" }}>
        Rank 1000 · $10B
      </div>
    </motion.div>
  );
};

export const Hero = ({ liveCount }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    e.currentTarget.style.setProperty("--sx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden pt-[72px]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07070b]/60 via-[#07070b]/70 to-[#07070b]" />
      </motion.div>
      <div className="grid-lines pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(500px circle at var(--sx, 70%) var(--sy, 40%), rgba(162,89,255,0.10), transparent 60%)" }} />
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
        className="absolute left-0 right-0 top-[72px] h-px origin-left bg-gradient-to-r from-[#ff3dc8] via-[#a259ff] to-transparent shadow-[0_0_20px_#a259ff]"
      />

      <div className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-14 md:px-10 lg:grid-cols-12 lg:pt-20">
        <motion.div style={{ y: fgY }} className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-violet-300"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#e9d5ff] shadow-[0_0_12px_#e9d5ff]" />
            OGM // Modded GTA V accounts
          </motion.p>
          <HeroTitle />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.2 }} className="mt-10 max-w-xl">
            <p className="text-base text-slate-300 md:text-lg">
              Pre-loaded GTA Online accounts with the money, rank, cars and outfits already stacked. Pick your PC account, pay your way, and jump straight into Los Santos.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollToId("catalog")}
                data-testid="hero-browse-button"
                className="group flex items-center gap-3 rounded-full bg-[#ff3dc8] px-7 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,61,200,0.6)]"
              >
                Browse accounts
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
              </button>
              <button onClick={() => scrollToId("how-it-works")} data-testid="hero-how-button" className="rounded-full border border-white/20 px-7 py-4 font-mono text-sm uppercase tracking-widest text-white transition-colors hover:border-[#a259ff] hover:text-[#a259ff]">
                How it works
              </button>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-4 lg:pt-10">
          <div className="mx-auto max-w-sm lg:max-w-none">
            <TiltFrame mx={mx} my={my} />
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="relative mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4 border-t border-white/10 px-5 py-6 md:px-10">
        <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-widest text-slate-300">
          <span className="flex items-center gap-2"><Monitor className="h-4 w-4 text-violet-300" /> PC accounts</span>
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-300" data-testid="hero-live-count">
          <span className="text-[#e9d5ff]">{liveCount}</span> accounts in stock now
        </p>
      </motion.div>
    </section>
  );
};
